import type { ReactNode } from 'react';
import { SectionLabel } from '@siliconx/shared/ui/SectionLabel';
import { BasisTag } from '../BasisTag';
import { RegulationChip } from '../RegulationChip';
import { FlowSankey } from '../../charts/FlowSankey';
import { type FlowSankeyLinkSpec, type FlowSankeyNodeSpec } from '../../charts/flowSankeyLayout';
import { formatCarbonMass } from './circularityFormat';
import {
  CIRCULARITY_AVOIDED_VS_NEW_LABEL,
  CIRCULARITY_CAT5_BURDEN_LABEL,
  CIRCULARITY_DISPOSAL_LEG_LABEL,
  CIRCULARITY_FLEET_EMBODIED_LABEL,
  CIRCULARITY_REPURPOSE_LEG_LABEL,
  CIRCULARITY_RESALE_LEG_LABEL,
  CIRCULARITY_RETAINED_IN_FLEET_LABEL,
  CIRCULARITY_REUSE_BURDENS_LABEL,
} from './circularityCopy';
import type { SankeyLegView, useLifecycleSankey } from '../../../hooks/useEnvironmentalCircularity';

type SankeyData = NonNullable<ReturnType<typeof useLifecycleSankey>['data']>;

// Sized so SVG text renders near-native (~11-12px) at the panel's typical
// ~1100-1250px width; the max-w cap below stops ultrawide screens from
// re-inflating labels (the 640-wide viewBox shipped in SIL-516 rendered at
// ~1.9x, making node labels compete with the page h1).
const SVG_WIDTH = 1080;
const SVG_HEIGHT = 340;
const RIGHT_MARGIN = 250;

/**
 * SIL-516 — pure spec builder: `SankeyData` (the hook's real+hybrid legs) to
 * a generic `FlowSankey` spec. Kept separate from the component so the
 * conservation invariants (fleet total = sum of legs; each leg = sum of its
 * outcomes) are exercised directly in tests without rendering React.
 *
 * Stage 1→2 (fleet → leg) is unchanged from the shipped single-stage
 * component. Stage 2→3 (new, per SIL-516):
 * - Resale → "Avoided vs new" (quarantined, SIL-459 — the advisory figure
 *   never merges with a footprint node) + "Reuse burdens", the DERIVED
 *   remainder `embodiedKgco2e − avoidedVsNewKgco2e` (transport + refurb);
 *   avoided + burdens sums back to the resale leg's embodied kg exactly.
 * - Repurpose → "Retained in fleet", a 1:1 pass-through of the leg's
 *   embodied kg (the embodied carbon carried by assets kept in service).
 * - Disposal → "Cat-5 disposal burden" — `cat5BurdenKgco2e` is a distinct,
 *   much smaller quantity than the leg's embodied kg (a burden figure, not
 *   a share of the embodied stock), so it is deliberately NOT required to
 *   sum back to `disposal.embodiedKgco2e`. At an org with zero disposed
 *   assets this link (and the leg's own fleet→disposal link) renders at the
 *   floor and is annotated "≈0" — never inflated by the old MIN_SHARE
 *   floor this replaces.
 */
export function buildLifecycleSankeySpec(data: SankeyData): {
  nodes: FlowSankeyNodeSpec[];
  links: FlowSankeyLinkSpec[];
} {
  const { resale, repurpose, disposal } = data;
  const reuseBurdensKgco2e = Math.max(0, resale.embodiedKgco2e - resale.avoidedVsNewKgco2e);
  // Fleet asset count deliberately excludes the resale leg when it is
  // SAMPLE-basis (SIL-471 hybrid fallback) — never mix a sample count into
  // the headline "Fleet" total, matching the shipped component's rule.
  const fleetAssetCount = repurpose.assetCount + disposal.assetCount + (resale.basis === 'sample' ? 0 : resale.assetCount);

  const nodes: FlowSankeyNodeSpec[] = [
    {
      id: 'fleet',
      col: 0,
      label: CIRCULARITY_FLEET_EMBODIED_LABEL,
      cap: `${fleetAssetCount} assets`,
      fillClass: 'fill-zinc-500',
    },
    {
      id: 'resale',
      col: 1,
      label: CIRCULARITY_RESALE_LEG_LABEL,
      cap: `${resale.assetCount} assets`,
      fillClass: 'fill-sky-500',
      striped: resale.basis === 'sample',
    },
    {
      id: 'repurpose',
      col: 1,
      label: CIRCULARITY_REPURPOSE_LEG_LABEL,
      cap: `${repurpose.assetCount} assets`,
      fillClass: 'fill-emerald-500',
    },
    {
      id: 'disposal',
      col: 1,
      label: CIRCULARITY_DISPOSAL_LEG_LABEL,
      cap: `${disposal.assetCount} assets`,
      fillClass: 'fill-amber-500',
    },
    {
      id: 'avoided',
      col: 2,
      label: CIRCULARITY_AVOIDED_VS_NEW_LABEL,
      cap: 'Advisory',
      fillClass: 'fill-sky-500',
      striped: resale.basis === 'sample',
      quarantine: true,
    },
    {
      id: 'burden',
      col: 2,
      label: CIRCULARITY_REUSE_BURDENS_LABEL,
      cap: 'Transport + refurb',
      fillClass: 'fill-amber-500',
      striped: resale.basis === 'sample',
    },
    {
      id: 'retained',
      col: 2,
      label: CIRCULARITY_RETAINED_IN_FLEET_LABEL,
      cap: 'Embodied stock',
      fillClass: 'fill-emerald-500',
    },
    {
      id: 'cat5',
      col: 2,
      label: CIRCULARITY_CAT5_BURDEN_LABEL,
      cap: 'Never avoided/displaced',
      fillClass: 'fill-amber-500',
    },
  ];

  const links: FlowSankeyLinkSpec[] = [
    { source: 'fleet', target: 'resale', value: resale.embodiedKgco2e, fillClass: 'fill-sky-500', striped: resale.basis === 'sample' },
    { source: 'fleet', target: 'repurpose', value: repurpose.embodiedKgco2e, fillClass: 'fill-emerald-500' },
    { source: 'fleet', target: 'disposal', value: disposal.embodiedKgco2e, fillClass: 'fill-amber-500' },
    { source: 'resale', target: 'avoided', value: resale.avoidedVsNewKgco2e, fillClass: 'fill-sky-500', striped: resale.basis === 'sample' },
    { source: 'resale', target: 'burden', value: reuseBurdensKgco2e, fillClass: 'fill-amber-500', striped: resale.basis === 'sample' },
    { source: 'repurpose', target: 'retained', value: repurpose.embodiedKgco2e, fillClass: 'fill-emerald-500' },
    { source: 'disposal', target: 'cat5', value: disposal.cat5BurdenKgco2e, fillClass: 'fill-amber-500' },
  ];

  return { nodes, links };
}

/**
 * SIL-473 (shipped), upgraded SIL-516 — hand-drawn multi-stage lifecycle
 * Sankey built on the generic `FlowSankey` engine (`components/charts/`):
 * fleet embodied carbon fans out into three disposition legs (stage 1→2,
 * unchanged), each of which fans out again into its real-world outcome
 * (stage 2→3, new — see `buildLifecycleSankeySpec` above for exactly how
 * each leg's outcome is derived and why disposal is the one exception to
 * "each leg sums to its outcomes").
 *
 * The resale leg stays HYBRID (SIL-471): real when the org has completed
 * sales, sample fallback otherwise (`data.resale.basis === 'sample'`, never
 * hardcoded) — its diagonal-stripe fill, and the derived "Reuse burdens"
 * outcome that shares its basis, both key off that one field. A real resale
 * leg renders solid, exactly like repurpose/disposal.
 *
 * The "Avoided vs new" outcome node sits inside `FlowSankey`'s dashed sky
 * quarantine boundary (SIL-459) — its ribbon never merges with, and this
 * component never sums it into, any footprint figure.
 *
 * SIL-516 replaced the old MIN_SHARE=0.14 rendering floor (which inflated a
 * literally-empty disposal leg into a ~12% band) with `FlowSankey`'s 3px
 * floor + "≈0" annotation — floors stay a rendering choice, never applied
 * to the annotated figures themselves.
 *
 * Both light/dark theme safe: every fill is a literal Tailwind ramp class
 * from the set `apps/customer/src/styles/globals.css` remaps under `.dark`
 * (emerald/amber/sky/zinc) — same convention `CarbonAdvisoryModule` already
 * relies on for its own sky treatment.
 *
 * Validated against a design-eval prototype (real Meta demo-org data, not
 * shipped UI): https://claude.ai/code/artifact/5180263f-c968-4844-8156-60897f64e587
 * (candidate A) — see SIL-516.
 */
export function LifecycleSankey({ data }: { data: SankeyData }) {
  const spec = buildLifecycleSankeySpec(data);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <SectionLabel>Historical lifecycle flows</SectionLabel>
      </div>

      <FlowSankey
        className="max-w-[1240px]"
        {...spec}
        width={SVG_WIDTH}
        height={SVG_HEIGHT}
        rightMargin={RIGHT_MARGIN}
        format={formatCarbonMass}
        ariaLabel="Lifecycle flows: fleet fans out into resale, repurpose, and disposal legs, each into its outcome"
      />

      <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-zinc-500">
        <BasisRow label={CIRCULARITY_RESALE_LEG_LABEL} basis={data.resale.basis} />
        <BasisRow label={CIRCULARITY_REPURPOSE_LEG_LABEL} basis={data.repurpose.basis} />
        {/* SIL-611: the Disposal node's regulation chip. Sits on the legend
         * row rather than inside the SVG (the node label itself isn't a
         * link target), still visually attached to the disposal leg it
         * governs. */}
        <BasisRow label={CIRCULARITY_DISPOSAL_LEG_LABEL} basis={data.disposal.basis}>
          <RegulationChip regulationKey="ca_uwed" />
        </BasisRow>
      </div>
    </div>
  );
}

function BasisRow({
  label,
  basis,
  children,
}: {
  label: string;
  basis: SankeyLegView['basis'];
  children?: ReactNode;
}) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span>{label}</span>
      <BasisTag basis={basis} />
      {children}
    </span>
  );
}
